/**
 * @fileoverview Session Key Manager - Secure caching of decrypted master keys with persistence
 *
 * This service provides caching of the decrypted master key during a user session,
 * with optional persistence to IndexedDB for seamless restoration across browser
 * sessions (page refresh, browser close/reopen).
 *
 * **Architecture:**
 * - Caches the decrypted AES-256 master key in memory for fast access
 * - Persists encrypted session to IndexedDB for cross-session restoration
 * - Supports WebAuthn-based session unlocking (primary)
 * - Supports password-based fallback
 * - Configurable session duration per user
 * - Auto-expires sessions based on duration
 * - Clears on logout (both memory and IndexedDB)
 *
 * **Security Design:**
 * - In-memory cache for immediate operations
 * - IndexedDB stores master key encrypted with a random session key
 * - Both session key and encrypted master key stored in IndexedDB
 * - Automatic expiration based on user-configured duration
 * - Separate cache per user to prevent cross-user access
 * - Full cleanup on logout
 *
 * **Persistence Flow:**
 * 1. User unlocks with password/WebAuthn
 * 2. Master key cached in memory + encrypted and persisted to IndexedDB
 * 3. On page refresh: restore from IndexedDB automatically
 * 4. On logout: clear memory + delete from IndexedDB
 *
 * @module services/SessionKeyManager
 */

import { createLogger } from '@xanectech/lib';
import {
  importMasterKey,
  decryptMasterKeyWithPassword,
  type EncryptedMasterKey,
} from './EncryptionService';
import {
  persistSession,
  restoreSession,
  clearPersistedSession,
  hasPersistedSession,
} from './SessionPersistenceService';
// import { supabase } from '@xanectech/supabase-client';

const logger = createLogger('SessionKeyManager');

/**
 * Structure for cached master key in memory
 */
interface CachedMasterKey {
  masterKey: CryptoKey;
  masterKeyMaterial: Uint8Array; // Raw bytes for persistence
  cachedAt: Date;
  userId: string;
}

// In-memory cache
let cachedMasterKey: CachedMasterKey | null = null;
// Session duration in minutes (loaded per-user from database)
let sessionDuration: number = 60; // Default: 60 minutes

// Restoration lock to prevent concurrent restoration attempts
let restorationPromise: Promise<boolean> | null = null;
let isRestoringNow = false;

/**
 * Load the user's encryption session duration preference from the database
 *
 * @param userId - User ID to load session duration for
 */
export async function loadSessionDuration(userId: string): Promise<void> {
  try {
    const { supabase } = await import('@xanectech/supabase-client');
    const { data: profile, error } = await supabase
      .from('user_profiles')
      .select('encryption_session_duration')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) throw error;

    if (profile?.encryption_session_duration != null) {
      sessionDuration = profile.encryption_session_duration;
    } else {
      sessionDuration = 60;
    }
  } catch (error) {
    logger.error(
      'Failed to load session duration, using secure default',
      error as Error,
    );
    sessionDuration = 60;
  }
}

/**
 * Initialize the session with password-based master key decryption
 *
 * Decrypts the master key, caches it in memory, and persists to IndexedDB
 * for seamless restoration across browser sessions.
 *
 * @param userId - User ID to initialize session for
 * @param password - User's password for decrypting the master key
 * @param encryptedMasterKey - Encrypted master key bundle
 */
export async function initializeSessionWithPassword(
  userId: string,
  password: string,
  encryptedMasterKey: EncryptedMasterKey,
): Promise<void> {
  // Load session duration from database first
  await loadSessionDuration(userId);

  // Decrypt master key with password
  const masterKeyMaterial = await decryptMasterKeyWithPassword(
    encryptedMasterKey,
    password,
  );

  // Import the decrypted master key
  const masterKey = await importMasterKey(masterKeyMaterial);

  // Cache in memory
  cachedMasterKey = {
    masterKey,
    masterKeyMaterial,
    cachedAt: new Date(),
    userId,
  };

  // Persist to IndexedDB for cross-session restoration
  try {
    await persistSession(userId, masterKeyMaterial, sessionDuration);
  } catch (error) {
    logger.error('Failed to persist session to IndexedDB', error as Error);
    // Don't fail the unlock - in-memory session still works
  }
}

/**
 * Initialize session directly with an already-decrypted master key
 *
 * Used when the master key has already been decrypted by another mechanism
 * (e.g., WebAuthn, biometric authentication, or session restoration).
 *
 * @param userId - User ID to initialize session for
 * @param masterKey - Already-decrypted master key
 * @param skipPersistence - If true, skip persisting to IndexedDB (used during restore)
 */
export async function initializeSessionWithMasterKey(
  userId: string,
  masterKey: CryptoKey,
  skipPersistence: boolean = false,
): Promise<void> {
  logger.info(
    '[SessionKeyManager] initializeSessionWithMasterKey called for userId:',
    { userId },
  );

  // Load session duration from database first
  await loadSessionDuration(userId);

  // Export raw key material for persistence
  let masterKeyMaterial: Uint8Array;
  try {
    const exportedKey = await window.crypto.subtle.exportKey('raw', masterKey);
    masterKeyMaterial = new Uint8Array(exportedKey);
  } catch (error) {
    logger.error('Failed to export master key material', error as Error);
    throw new Error('Failed to export master key for persistence');
  }

  // Cache in memory
  cachedMasterKey = {
    masterKey,
    masterKeyMaterial,
    cachedAt: new Date(),
    userId,
  };

  // Persist to IndexedDB (unless restoring from IndexedDB)
  if (!skipPersistence) {
    try {
      await persistSession(userId, masterKeyMaterial, sessionDuration);
    } catch (error) {
      logger.error('Failed to persist session to IndexedDB', error as Error);
      // Don't fail - in-memory session still works
    }
  }

  logger.info('[SessionKeyManager] Session cached, verifying:', {
    status: getCachedMasterKey(userId) ? 'SUCCESS' : 'FAILED',
  });
}

/**
 * Attempt to restore session from IndexedDB
 *
 * Called on application load to check for and restore a valid persisted session.
 * Returns true if session was successfully restored, false otherwise.
 *
 * @param userId - User ID to restore session for
 * @returns True if session was restored, false if user needs to unlock
 */
export async function tryRestoreSession(userId: string): Promise<boolean> {
  // If restoration is already in progress, wait for it
  if (restorationPromise && isRestoringNow) {
    logger.info('[SessionKeyManager] 🔄 Restoration in progress, waiting...');
    return await restorationPromise;
  }

  // Already have a valid in-memory session?
  if (cachedMasterKey && cachedMasterKey.userId === userId) {
    const elapsed = Date.now() - cachedMasterKey.cachedAt.getTime();
    const durationMs = sessionDuration * 60 * 1000;
    if (durationMs === 0 || elapsed < durationMs) {
      logger.info('[SessionKeyManager] ✅ In-memory session still valid');
      return true;
    }
  }

  // Start restoration with lock
  isRestoringNow = true;
  logger.info('[SessionKeyManager] 🔒 Starting session restoration...');

  restorationPromise = (async () => {
    try {
      const masterKeyMaterial = await restoreSession(userId);

      if (!masterKeyMaterial) {
        logger.info('[SessionKeyManager] ❌ No persisted session found');
        return false;
      }

      logger.info(
        '[SessionKeyManager] 📦 Session data found, importing key...',
      );

      const masterKey = await importMasterKey(masterKeyMaterial);
      await loadSessionDuration(userId);

      cachedMasterKey = {
        masterKey,
        masterKeyMaterial,
        cachedAt: new Date(),
        userId,
      };

      logger.info(
        '[SessionKeyManager] ✅ Session restored and cached successfully',
      );

      try {
        await persistSession(userId, masterKeyMaterial, sessionDuration);
      } catch (error) {
        logger.error(
          'Failed to update persisted session timestamp',
          error as Error,
        );
      }

      return true;
    } catch (error) {
      logger.error('Failed to restore session', error as Error);
      logger.info('[SessionKeyManager] ❌ Session restoration failed:', {
        error: String(error),
      });
      return false;
    } finally {
      isRestoringNow = false;
      restorationPromise = null;
    }
  })();

  return await restorationPromise;
}

/**
 * Check if a valid persisted session exists without restoring it
 *
 * Useful for UI decisions before attempting full restoration.
 *
 * @param userId - User ID to check
 * @returns True if a valid persisted session exists
 */
export async function hasValidPersistedSession(
  userId: string,
): Promise<boolean> {
  // Check in-memory first
  if (cachedMasterKey && cachedMasterKey.userId === userId) {
    const elapsed = Date.now() - cachedMasterKey.cachedAt.getTime();
    const durationMs = sessionDuration * 60 * 1000;
    if (durationMs === 0 || elapsed < durationMs) {
      return true;
    }
  }

  // Check IndexedDB
  return await hasPersistedSession(userId);
}

/**
 * Retrieve the cached master key if available and not expired
 *
 * @param userId - User ID to retrieve cached key for
 * @returns Decrypted master key, or null if not available/expired
 */
export function getCachedMasterKey(userId: string): CryptoKey | null {
  logger.info('[SessionKeyManager] getCachedMasterKey called for userId:', {
    userId,
  });
  logger.info('[SessionKeyManager] cachedMasterKey info:', {
    exists: !!cachedMasterKey,
    cachedUserId: cachedMasterKey?.userId,
  });

  if (!cachedMasterKey || cachedMasterKey.userId !== userId) {
    logger.info(
      '[SessionKeyManager] Returning null - no cache or user mismatch',
    );
    return null;
  }

  // Use the loaded session duration
  const CACHE_DURATION_MS = sessionDuration * 60 * 1000;
  const elapsed = Date.now() - cachedMasterKey.cachedAt.getTime();

  if (CACHE_DURATION_MS > 0 && elapsed > CACHE_DURATION_MS) {
    logger.info('[SessionKeyManager] Key expired, clearing');
    clearSession();
    return null;
  }

  logger.info('[SessionKeyManager] Returning valid cached master key');
  return cachedMasterKey.masterKey;
}

/**
 * Retrieve cached raw master key material if available and not expired
 *
 * @param userId - User ID to retrieve cached key material for
 * @returns Copy of raw master key bytes, or null if not available/expired
 */
export function getCachedMasterKeyMaterial(userId: string): Uint8Array | null {
  const key = getCachedMasterKey(userId);

  if (!key || !cachedMasterKey || cachedMasterKey.userId !== userId) {
    return null;
  }

  return new Uint8Array(cachedMasterKey.masterKeyMaterial);
}

/**
 * Check whether the session is currently initialized for a user
 *
 * @param userId - User ID to check initialization status for
 * @returns True if session is initialized for this user
 */
export function isSessionInitialized(userId: string): boolean {
  return cachedMasterKey !== null && cachedMasterKey.userId === userId;
}

/**
 * Ensure the session is unlocked, attempting automatic restoration if needed
 *
 * @param userId - User ID to ensure session is unlocked for
 * @param maxRetries - Maximum number of retry attempts (default 3)
 * @returns True if session is unlocked, false if user needs to authenticate
 */
export async function ensureSessionUnlocked(userId: string): Promise<boolean> {
  // Check if already unlocked
  if (isSessionInitialized(userId)) {
    const key = getCachedMasterKey(userId);
    if (key) {
      return true;
    }
  }

  // Try to restore (this handles the locking internally)
  return await tryRestoreSession(userId);
}

/**
 * Clear the session cache and persisted data
 *
 * Called on user logout. Clears both in-memory cache and IndexedDB.
 */
export function clearSession(): void {
  const userId = cachedMasterKey?.userId;

  // Clear in-memory cache
  cachedMasterKey = null;
  sessionDuration = 60; // Reset to default
  restorationPromise = null;
  isRestoringNow = false;

  // Clear IndexedDB (async, fire-and-forget)
  if (userId) {
    clearPersistedSession(userId).catch((error) => {
      logger.error(
        'Failed to clear persisted session on logout',
        error as Error,
      );
    });
  }
}

/**
 * Clear session for a specific user (async version for explicit cleanup)
 *
 * @param userId - User ID to clear session for
 */
export async function clearSessionForUser(userId: string): Promise<void> {
  // Clear in-memory if it's this user
  if (cachedMasterKey?.userId === userId) {
    cachedMasterKey = null;
    sessionDuration = 60;
  }

  // Clear from IndexedDB
  await clearPersistedSession(userId);
}

/**
 * Get detailed status information about the session cache
 *
 * @param userId - User ID to get cache status for
 * @returns Cache status object
 */
export function getSessionStatus(userId: string): {
  isInitialized: boolean;
  cachedAt: Date | null;
  age: number | null;
  remainingMinutes: number | null;
} {
  if (!cachedMasterKey || cachedMasterKey.userId !== userId) {
    return {
      isInitialized: false,
      cachedAt: null,
      age: null,
      remainingMinutes: null,
    };
  }

  const age = Date.now() - cachedMasterKey.cachedAt.getTime();
  const durationMs = sessionDuration * 60 * 1000;
  const remainingMs = durationMs > 0 ? Math.max(0, durationMs - age) : null;

  return {
    isInitialized: true,
    cachedAt: cachedMasterKey.cachedAt,
    age,
    remainingMinutes:
      remainingMs !== null ? Math.floor(remainingMs / 60000) : null,
  };
}

/**
 * Get the current session duration in minutes
 */
export function getSessionDuration(): number {
  return sessionDuration;
}

/**
 * Export the cached master key as raw bytes
 *
 * Used during WebAuthn credential registration to wrap the raw master key.
 *
 * @param userId - User ID to export key for
 * @returns Raw key bytes or null if no key cached
 */
export async function exportCachedMasterKey(
  userId: string,
): Promise<Uint8Array | null> {
  if (!cachedMasterKey || cachedMasterKey.userId !== userId) {
    return null;
  }

  // Return the already-stored material (no need to re-export)
  return cachedMasterKey.masterKeyMaterial;
}
